import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { OrgSwitcher } from "./OrgSwitcher";
import { GuidanceToggle } from "./GuidanceToggle";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { UserMenu } from "./UserMenu";

export function Header({ onMenuToggle }: { onMenuToggle?: () => void }) {
  return (
    <header className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-3">
          {onMenuToggle ? (
            <button
              type="button"
              onClick={onMenuToggle}
              aria-label="Open menu"
              className="rounded p-1.5 text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-200 dark:hover:bg-slate-800 md:hidden"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
                className="h-5 w-5"
              >
                <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}
          <Brand />
          <OrgSwitcher />
        </div>
        <div className="hidden flex-1 items-center justify-center md:flex">
          <SearchBarPlaceholder />
        </div>
        <div className="flex items-center gap-3">
          <GuidanceToggle />
          <ThemeSwitcher />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

function Brand() {
  const { activeOrganisation } = useAuth();
  const { resolved } = useTheme();
  const [logoFailed, setLogoFailed] = useState(false);
  const slug = activeOrganisation?.slug;
  const base = import.meta.env.VITE_API_URL ?? "/api/v1";
  const logoUrl = slug
    ? `${base}/branding/${slug}/${resolved === "dark" ? "dark-logo" : "light-logo"}`
    : null;
  const showCustomLogo = Boolean(logoUrl) && !logoFailed;

  if (showCustomLogo && logoUrl) {
    return (
      <a
        href="/"
        className="flex items-center gap-2 text-base font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-100"
      >
        <img
          src={logoUrl}
          alt={`${activeOrganisation?.name ?? "Organisation"} logo`}
          onError={() => setLogoFailed(true)}
          className="h-8 max-w-[160px] object-contain"
        />
      </a>
    );
  }

  return (
    <a
      href="/"
      className="flex items-center gap-2 text-base font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-100"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-8 w-8 items-center justify-center rounded bg-brand-600 text-white"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          className="h-5 w-5"
        >
          <path d="M4 7l8-4 8 4v10l-8 4-8-4z" />
          <path d="M4 7l8 4 8-4M12 11v10" />
        </svg>
      </span>
      ISO Clarity
    </a>
  );
}

function SearchBarPlaceholder() {
  return (
    <div className="relative w-full max-w-xl">
      <input
        type="search"
        placeholder="Find evidence, clauses or logs… (search coming in a later release)"
        disabled
        aria-label="Search"
        className="w-full cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
      />
    </div>
  );
}
